import type { PropsWithChildren } from 'react'
import { useCallback, useMemo } from 'react'
import { useCountries } from '@/hooks/useCountries'
import { CountriesContext } from './CountriesContext'

interface CountriesProviderProps extends PropsWithChildren {
  prefetchOnMount?: boolean
}

export function CountriesProvider({ children, prefetchOnMount = true }: Readonly<CountriesProviderProps>) {
  const countriesState = useCountries({ prefetchOnMount })

  const countryByCode = useMemo(
    () => new Map(countriesState.countries.map(country => [country.code.toUpperCase(), country])),
    [countriesState.countries],
  )

  const getCountryByCode = useCallback(
    (code: string) => countryByCode.get(code.trim().toUpperCase()),
    [countryByCode],
  )

  const normalizeCountryCodes = useCallback((params: string[]) => {
    const normalized = new Set<string>()

    params.forEach((param) => {
      const code = param.trim().toUpperCase()

      if (countryByCode.has(code))
        normalized.add(code)
    })

    return [...normalized]
  }, [countryByCode])

  return (
    <CountriesContext
      value={{
        ...countriesState,
        getCountryByCode,
        normalizeCountryCodes,
      }}
    >
      {children}
    </CountriesContext>
  )
}
